import {
  DeliveryMethodModalProvider,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { useSelectCheapestQuoteMutation } from '@b-stock/fe-core/bstock-next/mutations'
import {
  getSelectedQuoteQuery,
  myLocationsQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { LocationAttribute } from '@b-stock/location-api-client'

import { useFirstDibsPurchase } from '@/buyer-portal/components/features/listing/hooks/useFirstDibsPurchase'
import { MissingDataBanner } from '@/buyer-portal/components/features/listing/MissingDataBanner'
import { FeatureFlags } from '@/helpers/featureFlags'

import { AutoPayMethodProvider } from './AutoPayMethodProvider'
import { BidConfirmationModalProvider } from './BidConfirmationModal/useBidConfirmationModal'
import { BidFormFormikProvider } from './BidFormFormikProvider'
import { BuyNowConfirmationModalProvider } from './BuyNowConfirmationModal/useBuyNowConfirmationModal'
import { hasAuctionEnded } from './hasAuctionEnded'
import {
  InternalBidFormProvider,
  type InternalBidFormProviderProps,
} from './InternalBidFormProvider'
import { useOnQuoteError } from './useOnQuoteError'

type BidFormProviderProps = Omit<InternalBidFormProviderProps, 'selectedQuote'>

export const BidFormProvider = (props: BidFormProviderProps) => {
  const { listing } = props

  if (!listing.shipping) {
    return (
      <MissingDataBanner
        titleId="Auction.details.actionColumn.missingData.title"
        messageId="Auction.details.actionColumn.missingData.shipping"
        log={{
          level: 'warn',
          message: 'BidFormProvider: listing missing shipping',
          metadata: {
            listingId: listing._id,
            missingField: 'shipping',
          },
        }}
      />
    )
  }

  if (!listing.auction._id) {
    return (
      <MissingDataBanner
        titleId="Auction.details.actionColumn.missingData.title"
        messageId="Auction.details.actionColumn.missingData.auction"
        log={{
          level: 'error',
          message: 'BidFormProvider: listing missing auction id',
          metadata: {
            listingId: listing._id,
            missingField: 'auction._id',
          },
        }}
      />
    )
  }

  return <BidFormProviderContent {...props} />
}

const BidFormProviderContent = (props: BidFormProviderProps) => {
  const {
    auctionDetailState: { auctionState },
    currency,
    formId,
    initialBidAmount,
    initialBidType,
    listing,
  } = props

  // FP-1752: Use single source for accountId to prevent query key mismatch.
  // useAccountId() and useDecodedAccessToken()?.accountId can diverge during token
  // refresh. Both query keys and DeliveryMethodModalProvider must use same value.
  const decodedToken = useDecodedAccessToken({ required: false })
  const accountId = decodedToken?.accountId
  const onQuoteError = useOnQuoteError()
  const { mutateAsync: selectCheapestQuote } = useSelectCheapestQuoteMutation()
  const isGeofenceEnabled = useFlag(FeatureFlags.GEOFENCED_LISTINGS)

  const myLocations = myLocationsQuery.useWithOpts(
    [
      {
        attributes: [LocationAttribute.DefaultDeliverable],
        ...(accountId && { accountId: [accountId] }),
      },
    ],
    { enabled: props.auctionDetailState.eligibleToBid }
  )
  const defaultLocation = myLocations.data?.locations[0]

  const { data: selectedQuote } = getSelectedQuoteQuery.useWithOpts(
    [listing._id, accountId],
    {
      enabled:
        props.auctionDetailState.eligibleToBid &&
        (!!defaultLocation?.id || myLocations.isFetched),
    }
  )

  const { earlyPrice: fdEarlyPrice } = useFirstDibsPurchase(listing)

  return (
    <BidConfirmationModalProvider>
      <BuyNowConfirmationModalProvider>
        <DeliveryMethodModalProvider
          accountId={accountId ?? ''}
          isEnded={hasAuctionEnded(auctionState)}
          isGeofenceEnabled={isGeofenceEnabled}
          listing={listing}
          onQuoteError={onQuoteError}
          originLocation={listing.lot.location}
          selectCheapestQuote={selectCheapestQuote}
          selectedQuote={selectedQuote}
        >
          <BidFormFormikProvider
            currency={currency}
            formId={formId}
            initialBidAmount={initialBidAmount}
            initialBidType={initialBidType}
            listing={listing}
          >
            <AutoPayMethodProvider
              listing={listing}
              selectedQuote={selectedQuote}
              sellerSubscription={props.sellerSubscription}
              buyNowPrice={
                // First Dibs buy-now charges the group's early price.
                fdEarlyPrice ??
                (listing.auction.buyNow?.enabled
                  ? listing.auction.buyNow.price
                  : undefined)
              }
            >
              <InternalBidFormProvider
                {...props}
                selectedQuote={selectedQuote}
              />
            </AutoPayMethodProvider>
          </BidFormFormikProvider>
        </DeliveryMethodModalProvider>
      </BuyNowConfirmationModalProvider>
    </BidConfirmationModalProvider>
  )
}
